import React from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
  Paper,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useTheme } from '../../contexts/ThemeContext';
import { AppWithConnectionStatus, ConnectedAppAccount } from '../../services/appsService';
import { formatDisplayDateTime } from '../../helpers/dateUtils';

export interface AppAccountManageModalProps {
  open: boolean;
  onClose: () => void;
  app: AppWithConnectionStatus | null;
  connectionDetails: ConnectedAppAccount | null;
  imageError?: boolean;
}

/**
 * Resolves connected account display from connection_settings.
 * For Odoo, prefer username; otherwise fallback to email_id/domain/email.
 */
function getConnectedAccountValue(conn: ConnectedAppAccount, appKey?: string): string {
  const settings =
    typeof conn.connection_settings === 'string'
      ? (() => {
          try {
            return JSON.parse(conn.connection_settings);
          } catch {
            return {};
          }
        })()
      : conn.connection_settings || {};
  if (appKey === 'odoo') {
    return settings.username || settings.email_id || settings.email || '—';
  }
  return settings.email_id || settings.domain || settings.email || '—';
}

/**
 * Resolves "Connected by" display from user_metadata (name or email_id), fallback to connected_by id.
 */
function getConnectedByDisplay(conn: ConnectedAppAccount | null): string {
  if (!conn) return '—';
  const meta = conn.user_metadata;
  if (meta && (meta.name || meta.email_id)) {
    return meta.name || meta.email_id || conn.connected_by;
  }
  return conn.connected_by || '—';
}

const AppAccountManageModal: React.FC<AppAccountManageModalProps> = ({
  open,
  onClose,
  app,
  connectionDetails,
  imageError = false,
}) => {
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();

  if (!app) return null;

  // Derive display values from connection for profile card (user_metadata from app-accounts API)
  const status = connectionDetails?.connection_status ?? 'inactive';
  const statusLabel = status === 'active' ? 'Connected' : status;
  const emailOrDomain = connectionDetails ? getConnectedAccountValue(connectionDetails, app.app_key) : '—';
  const connectedOn = connectionDetails?.created_at
    ? formatDisplayDateTime(connectionDetails.created_at)
    : '—';
  const connectedByDisplay = getConnectedByDisplay(connectionDetails);
  const appIconSrc = app.app_key === 'odoo' ? (app.app_image || '/images/apps/odoo_crm.png') : app.app_image;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: muiTheme.palette.background.paper,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          App connection details
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 2 }}>
        {/* Profile card: app icon, name, description, connected status */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${muiTheme.palette.divider}`,
            backgroundColor: isDarkMode ? muiTheme.palette.grey[900] : muiTheme.palette.grey[50],
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            {/* App icon (reduced size) */}
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                backgroundColor: imageError
                  ? (isDarkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[200])
                  : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                overflow: 'hidden',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                color: imageError ? muiTheme.palette.text.secondary : 'transparent',
              }}
            >
              {imageError ? (
                app.app_name.charAt(0).toUpperCase()
              ) : (
                <img
                  src={appIconSrc}
                  alt={`${app.app_name} icon`}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 6 }}
                />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* App name and Connected chip on same row (reduced font sizes) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0, fontSize: '0.95rem' }}>
                  {app.app_name}
                </Typography>
                <Chip
                  label={statusLabel}
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    height: 20,
                    '& .MuiChip-label': { px: 0.75 },
                    backgroundColor:
                      status === 'active'
                        ? muiTheme.palette.success.light + '30'
                        : muiTheme.palette.grey[500] + '30',
                    color: status === 'active' ? muiTheme.palette.success.dark : muiTheme.palette.text.secondary,
                    fontWeight: 500,
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: muiTheme.palette.text.secondary, fontSize: '0.8rem' }}>
                {app.app_description || app.category || '—'}
              </Typography>
            </Box>
          </Box>

          {/* Connected account details */}
          <Box
            sx={{
              borderTop: `1px solid ${muiTheme.palette.divider}`,
              pt: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <DetailRow label="Connected Account" value={emailOrDomain} />
            <DetailRow label="Connected on" value={connectedOn} />
            <DetailRow label="Connected by" value={connectedByDisplay} />
          </Box>
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

/** Single row: label in first column, value in second column so all values align vertically. */
function DetailRow({ label, value }: { label: string; value: string }) {
  const muiTheme = useMuiTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, textAlign: 'left' }}>
      <Typography variant="body2" sx={{ color: muiTheme.palette.text.secondary, fontWeight: 500, minWidth: 140, flexShrink: 0 }}>
        {label}:
      </Typography>
      <Typography variant="body2" sx={{ color: muiTheme.palette.text.primary, fontWeight: 600, textAlign: 'left' }}>
        {value}
      </Typography>
    </Box>
  );
}

export default AppAccountManageModal;
